import type { AudioTrack, GenerationDto, PostDto, VersionDto, MediaDto } from '@str/api/types';
import type { Channel, PostTemplate, ReelJobDto, ReelRequest, RenderInput } from '@str/shared';
import { api, unwrap, uploadFile } from './api';
import { postsApi } from './queries';
import { exportOverlay } from './picture-render';
import { storeCardAsset } from './composer-cards';
import { corsSource } from './cors-source';
import { defaultReelSound } from './reel-sound';
export type ReelDraft = Pick<
  RenderInput,
  'trim' | 'coverMs' | 'audio' | 'layers' | 'clips' | 'transition' | 'layerTimings' | 'sound'
> & {
  audioFile?: File;
  audioSize?: number;
  clipFiles?: Record<string, File>;
  clipInfo?: Record<string, { name: string; durationMs: number; size: number }>;
};
export function reelSource(version: VersionDto, library: Pick<MediaDto, 'id' | 'url'>[] = []) {
  const edit = version.videoEdit;
  return edit?.sourceMediaId
    ? library.find((media) => media.id === edit.sourceMediaId)?.url ||
        `/api/media/${edit.sourceMediaId}/file`
    : edit?.sourceUrl || version.videoUrl || '';
}
export function reelDraft(version: VersionDto): ReelDraft {
  const edit = version.videoEdit;
  return {
    trim: edit?.trim ?? { startMs: 0, endMs: 90000 },
    coverMs: edit?.coverMs ?? 0,
    audio: edit?.audio ?? null,
    layers: edit?.layers ?? [],
    ...(edit?.clips ? { clips: edit.clips } : {}),
    ...(edit?.transition ? { transition: edit.transition } : {}),
    ...(edit?.layerTimings ? { layerTimings: edit.layerTimings } : {}),
    ...(edit?.sound ? { sound: edit.sound } : {}),
  };
}
/**
 * Whether a draft is the edit the version already is, so Done has nothing to render. The trim
 * is compared after both are clamped to the loaded clip, because opening the editor clamps a
 * fresh draft's ninety-second default to the clip's length without the host touching anything
 * (2026-09-12: opening and closing the editor made a new version).
 */
export function sameReelEdit(draft: ReelDraft, rendered: ReelDraft, durationMs: number): boolean {
  const trim = (t: ReelDraft['trim']) => {
    const endMs = durationMs > 0 ? Math.min(t.endMs, durationMs) : t.endMs;
    return { startMs: Math.max(0, Math.min(t.startMs, endMs)), endMs };
  };
  const shape = (d: ReelDraft) =>
    JSON.stringify({
      trim: trim(d.trim),
      coverMs: d.coverMs,
      audio: d.audio,
      layers: d.layers,
      clips: d.clips ?? null,
      transition: d.transition ?? null,
      layerTimings: d.layerTimings ?? null,
      sound: d.sound ?? null,
    });
  return shape(draft) === shape(rendered);
}
export function clampTrim(
  trim: ReelDraft['trim'],
  edge: 'startMs' | 'endMs',
  value: number,
  duration: number,
) {
  const lower = edge === 'startMs' ? Math.max(0, trim.endMs - 90000) : trim.startMs + 3000;
  const upper = edge === 'startMs' ? trim.endMs - 3000 : Math.min(duration, trim.startMs + 90000);
  return { ...trim, [edge]: Math.round(Math.max(lower, Math.min(upper, value))) };
}
/** The MIME types the API stores audio under, keyed by every type or extension a browser reports. */
const AUDIO_MIME: Record<string, 'audio/mpeg' | 'audio/mp4' | 'audio/wav'> = {
  'audio/mpeg': 'audio/mpeg',
  'audio/mp3': 'audio/mpeg',
  mp3: 'audio/mpeg',
  'audio/mp4': 'audio/mp4',
  'audio/m4a': 'audio/mp4',
  'audio/x-m4a': 'audio/mp4',
  m4a: 'audio/mp4',
  'audio/wav': 'audio/wav',
  'audio/x-wav': 'audio/wav',
  'audio/wave': 'audio/wav',
  'audio/vnd.wave': 'audio/wav',
  wav: 'audio/wav',
};
/** Server-accepted MIME type for an audio file, or null when it is not MP3, M4A or WAV. */
export function reelAudioType(file: File) {
  const byType = AUDIO_MIME[file.type.toLowerCase()];
  if (byType) return byType;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return AUDIO_MIME[ext] ?? null;
}
export function checkReelAudio(file: File) {
  if (!reelAudioType(file)) throw new Error('Choose MP3, M4A, or WAV audio.');
  if (file.size > 25 * 1024 * 1024)
    throw new Error('Audio must be 25 MB or smaller. Choose a shorter or compressed file.');
}
/** The same bytes under a MIME type the API accepts; browsers report M4A and WAV inconsistently. */
export function reelAudioUpload(file: File) {
  const type = reelAudioType(file);
  return type && type !== file.type
    ? new File([file], file.name, { type, lastModified: file.lastModified })
    : file;
}
export function checkReelSize(
  draft: ReelDraft,
  addedClipBytes = 0,
  audioBytes = draft.audioFile?.size ?? draft.audioSize ?? 0,
) {
  const knownBytes = Object.values(draft.clipInfo ?? {}).reduce((sum, info) => sum + info.size, 0);
  if (knownBytes + addedClipBytes + audioBytes > 300 * 1024 * 1024)
    throw new Error(
      'Reel inputs must total 300 MB or less. Remove a clip or choose a smaller file.',
    );
}
export async function reelTracks(propertyId: string) {
  return (await unwrap<{ tracks: AudioTrack[] }>(api.api.audio.$get({ query: { propertyId } })))
    .tracks;
}

/** Ask for a bed in the reel's own length. Generates nothing else: the video is untouched. */
export async function generateReelMusic(postId: string, mood?: string) {
  return (
    await unwrap<{ media: MediaDto }>(
      api.api.posts[':id'].music.$post({
        param: { id: postId },
        json: mood ? { mood } : {},
      } as never),
    )
  ).media;
}
export async function renderReel(
  postId: string,
  propertyId: string,
  version: VersionDto,
  draft: ReelDraft,
  onUploaded?: (draft: ReelDraft) => void,
) {
  let prepared = { ...draft };
  for (const [id, file] of Object.entries(draft.clipFiles ?? {})) {
    const media = (await uploadFile(propertyId, file)).media;
    const clipFiles = { ...prepared.clipFiles };
    delete clipFiles[id];
    prepared = {
      ...prepared,
      clipFiles,
      clips: prepared.clips?.map((c) =>
        c.id === id ? { ...c, source: { kind: 'media' as const, mediaId: media.id } } : c,
      ),
    };
    onUploaded?.(prepared);
  }
  let audio = prepared.audio;
  if (draft.audioFile) {
    checkReelAudio(draft.audioFile);
    audio = {
      kind: 'media',
      mediaId: (await uploadFile(propertyId, reelAudioUpload(draft.audioFile))).media.id,
    };
    prepared = { ...prepared, audio, audioFile: undefined, audioSize: draft.audioFile.size };
    onUploaded?.(prepared);
  }
  const timings = prepared.layerTimings;
  const staticLayers = prepared.layers.filter(
    (layer) => !timings?.some((t) => t.layerId === layer.id),
  );
  const overlayMediaId = staticLayers.length
    ? (await storeCardAsset(postId, await exportOverlay(staticLayers))).id
    : undefined;
  const timedOverlays: NonNullable<RenderInput['timedOverlays']> = [];
  for (const timing of timings ?? []) {
    const layer = prepared.layers.find((l) => l.id === timing.layerId);
    if (!layer) throw new Error('A timed text item is missing. Reopen Text and try again.');
    timedOverlays.push({
      overlayMediaId: (await storeCardAsset(postId, await exportOverlay([layer]))).id,
      startMs: timing.startMs,
      endMs: timing.endMs,
    });
  }
  return (
    await unwrap<{ generation: GenerationDto }>(
      api.api.posts[':id'].render.$post({
        param: { id: postId },
        json: {
          parentVersionId: version.id,
          trim: draft.trim,
          coverMs: draft.coverMs,
          audio,
          layers: draft.layers,
          ...(overlayMediaId ? { overlayMediaId } : {}),
          ...(prepared.clips ? { clips: prepared.clips } : {}),
          ...(prepared.transition ? { transition: prepared.transition } : {}),
          ...(timings ? { layerTimings: timings, timedOverlays } : {}),
          // Music with untouched levels renders at the levels the editor played it at; the
          // renderer's own default would mute the clip under it.
          ...(prepared.sound || audio ? { sound: prepared.sound ?? defaultReelSound(true) } : {}),
        },
      }),
    )
  ).generation;
}
/** Center-crop like the delivery renderer, with no text baked into source frames. */
export function reelFrame(video: HTMLVideoElement, width = 270, opacity = 1) {
  if (!video.videoWidth || !video.videoHeight) return '';
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = Math.round((width * 16) / 9);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not read this video frame.');
  const scale = Math.max(canvas.width / video.videoWidth, canvas.height / video.videoHeight);
  context.fillStyle = '#000000';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.globalAlpha = Math.max(0, Math.min(1, opacity));
  context.drawImage(
    video,
    (canvas.width - video.videoWidth * scale) / 2,
    (canvas.height - video.videoHeight * scale) / 2,
    video.videoWidth * scale,
    video.videoHeight * scale,
  );
  context.globalAlpha = 1;
  return canvas.toDataURL('image/jpeg', 0.85);
}
export async function reelFilmstrip(url: string, duration: number, signal: AbortSignal) {
  const video = document.createElement('video');
  video.crossOrigin = 'anonymous';
  video.muted = true;
  video.preload = 'auto';
  const event = (name: string, action: () => void) =>
    new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        clearTimeout(timer);
        video.removeEventListener(name, done);
        video.removeEventListener('error', fail);
        signal.removeEventListener('abort', fail);
      };
      const done = () => {
        cleanup();
        resolve();
      };
      const fail = () => {
        cleanup();
        reject(new Error('Could not read video frames. Try reopening the Reel.'));
      };
      const timer = setTimeout(fail, 15000);
      video.addEventListener(name, done, { once: true });
      video.addEventListener('error', fail, { once: true });
      signal.addEventListener('abort', fail, { once: true });
      if (signal.aborted) fail();
      else action();
    });
  try {
    await event('loadeddata', () => {
      video.src = corsSource(url);
    });
    const frames: string[] = [];
    for (let i = 0; i < 6; i++) {
      const time = ((duration / 1000 - 0.05) * i) / 5;
      if (Math.abs(video.currentTime - time) > 0.001)
        await event('seeked', () => {
          video.currentTime = time;
        });
      frames.push(reelFrame(video, 90));
    }
    return frames;
  } finally {
    video.removeAttribute('src');
    video.load();
  }
}

/* ------------------------------------------------- a reel from the first screen */

export type ReelStartInput = {
  brief: string;
  channels: Channel[];
  template: PostTemplate | null;
  inspirationPostId: string | null;
};

/** The post that carries a reel: a Reel draft with the words, nothing made yet. */
export async function createReelPost(propertyId: string, input: ReelStartInput): Promise<PostDto> {
  return postsApi.create(propertyId, {
    format: 'Reel',
    goal: 'book',
    brief: input.brief,
    channels: input.channels,
    mediaMode: 'reimagine',
    referenceIds: [],
    pinnedIds: [],
    ...(input.template ? { template: input.template } : {}),
    ...(input.inspirationPostId ? { inspirationPostId: input.inspirationPostId } : {}),
  });
}

/** Starts a storyboard or a film on a reel post; the API charges it and answers with the job. */
export async function startReelJob(postId: string, input: ReelRequest): Promise<ReelJobDto> {
  return (
    await unwrap<{ job: ReelJobDto }>(
      fetch(`/api/posts/${postId}/reels`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      }),
    )
  ).job;
}
